import { Item } from './sharedTypes';
import { truncateISOString } from './dateTime';

export const nullStringCheck = (item: string | null | undefined): string => {
  if (!item) return '';
  return item;
};

export const camelCaseToUppercaseSentence = (str: string): string => {
  const splitText = str.replace(/([A-Z])/g, ' $1');
  return splitText.charAt(0).toUpperCase() + splitText.slice(1);
};

export const prettyPrintItem = (
  item: Item | string | undefined | null | number
): string => {
  if (!item) return 'None';
  if (typeof item === 'number') return '' + item;
  if (typeof item === 'string') return item;
  const result = Object.keys(item)
    .reduce((acc, cur) => {
      // Items to hide
      if (
        !item[cur] ||
        !cur ||
        cur === 'id' ||
        cur.toLowerCase().endsWith('id') ||
        cur === 'repairComments' ||
        cur === 'inventoryPartsUsed' ||
        cur === 'refreshToken' ||
        cur === 'createdAt' ||
        cur === 'updatedAt' ||
        cur.startsWith('moduleLicense')
      )
        return acc;
      // Reformat dates
      if (cur.toLowerCase().includes('date')) {
        return `${acc} ${camelCaseToUppercaseSentence(
          cur
          // @ts-ignore
        )}: ${truncateISOString(item[cur])}, `;
      }
      // Individual fields to format
      if (cur === 'company')
        return `${acc} Company: ${item[cur]?.companyName}, `;
      if (cur === 'companies')
        return `${acc} Companies: ${
          // @ts-ignore
          item[cur].length > 1 ? 'Multiple' : item[cur][0].companyName
        }, `;
      if (cur === 'hardwareCategory')
        // @ts-ignore
        return `${acc} Hardware Category: ${item[cur]?.categoryName}, `;
      if (cur === 'inventoryHardware')
        // @ts-ignore
        return `${acc} Inventory Hardware: ${item[cur]?.serial}, `;

      if (cur === 'order')
        // @ts-ignore
        return `${acc} Order: ${item[cur]?.purchaseOrderName}, `;
      if (cur === 'softwareVersion')
        // @ts-ignore
        return `${acc} Software Version: ${item[cur]?.name}, `;
      if (cur === 'software')
        return `${acc} Software Type: ${item[cur]?.type}, `;
      if (cur === 'hardwareType')
        return `${acc} Hardware Type: ${item[cur]?.typeName}, `;

      if (cur === 'modules' || cur === 'modulesToAdd')
        return `${acc} Modules: ${
          // @ts-ignore
          item[cur] && item[cur].length > 0 ? 'Yes' : 'No'
        }, `;
      if (cur === 'orders')
        return `${acc} Orders: ${
          // @ts-ignore
          item[cur] && item[cur].length > 0 ? 'Yes' : 'No'
        }, `;
      if (cur === 'supportPurchases')
        return `${acc} Support Purchases: ${
          // @ts-ignore
          item[cur] && item[cur].length > 0 ? 'Yes' : 'No'
        }, `;
      if (cur === 'module')
        // @ts-ignore
        return `${acc} Module: ${item[cur]?.moduleName}, `;
      if (cur === 'demos')
        return `${acc} Demos: ${
          // @ts-ignore
          item[cur] && item[cur].length > 0 ? 'Yes' : 'No'
        }, `;
      if (cur === 'hardware')
        return `${acc} Hardware: ${
          // @ts-ignore
          item[cur] && item[cur].length > 0 ? 'Yes' : 'No'
        }, `;
      if (cur === 'lptSold')
        return `${acc} Sold by LPT: ${
          // @ts-ignore
          typeof item[cur] === 'boolean'
            ? item[cur]
              ? 'Yes'
              : 'No'
            : item[cur] === 'true'
            ? 'Yes'
            : 'No'
        }, `;
      if (cur === 'softwareLinks')
        return `${acc} Software Links: ${
          // @ts-ignore
          item[cur] && item[cur].length > 0 ? 'Yes' : 'No'
        }, `;
      if (cur === 'softwareVersions')
        return `${acc} Software Versions: ${
          // @ts-ignore
          item[cur] && item[cur].length > 0 ? 'Yes' : 'No'
        }, `;
      if (cur === 'softwareSold')
        return `${acc} Software Sold: ${
          // @ts-ignore
          item[cur] && item[cur].length > 0 ? 'Yes' : 'No'
        }, `;
      if (cur === 'licenses')
        return `${acc} Licenses: ${
          // @ts-ignore
          item[cur] && item[cur].length > 0 ? 'Yes' : 'No'
        }, `;
      if (cur === 'rmas')
        return `${acc} Rmas: ${
          // @ts-ignore
          item[cur] && item[cur].length > 0 ? 'Yes' : 'No'
        }, `;
      if (cur === 'users')
        return `${acc} Users: ${
          // @ts-ignore
          item[cur] && item[cur].length > 0 ? 'Yes' : 'No'
        }, `;
      if (cur === 'role')
        return `${acc} Role: ${
          // @ts-ignore
          item[cur] === 1 ? 'Admin' : 'User'
        }, `;
      // Default
      return `${acc} ${camelCaseToUppercaseSentence(cur)}: ${item[cur]}, `;
    }, '')
    // Remove trailing comma and space
    .slice(0, -2);
  return result;
};

export const getManufacturerFromAnalyzerSerial = (
  serial: string
): string | undefined => {
  if (
    serial.startsWith('04') ||
    serial.startsWith('08') ||
    serial.startsWith('16') ||
    serial.startsWith('D4')
  )
    return 'LP Technologies';
  return undefined;
};

export const convertItemToPlural = (name: string): string => {
  switch (name) {
    case 'Company':
      return 'Companies';
    case 'RMA':
      return 'RMAs';
    case 'Part':
      return 'Parts';
    case 'Inventory Analyzer':
      return 'Inventory Analyzers';
    case 'Hardware Sold':
      return 'Hardware Sold';
    case 'Software Sold':
      return 'Software Sold';
    case 'Software Version':
      return 'Software Versions';
    case 'Software Link':
      return 'Software Links';
    case 'Analyzer Link':
      return 'Analyzer Links';
    case 'Support Purchase Record':
      return 'Support Purchase Records';
    case 'Record':
      return 'Records';
    case 'License':
      return 'Licenses';
    case 'Analyzer Type':
      return 'Analyzer Types';
    case 'Software Type':
      return 'Software Types';
    case 'Operating System':
      return 'Operating System';
    default:
      return name;
  }
};

export const convertStringTrueFalseToBoolean = (
  str: string | undefined
): boolean => {
  if (str === 'true') return true;
  if (str === 'false') return false;
  return true;
};
